import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { searchGlobally, type GlobalSearchGroup, type GlobalSearchHit } from '../../features/search/services/globalSearchService';

type SidebarGlobalSearchProps = {
  collapsed: boolean;
};

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const GROUP_LABEL_KEYS: Record<string, string> = {
  contacts: 'contacts',
  assets: 'assets',
  products: 'products',
  services: 'services',
  invoices: 'invoices',
  conversations: 'conversations',
  calendar: 'calendar',
  consultations: 'consultations',
  tasks: 'tasks',
  employees: 'teamMembers',
  knowledge: 'knowledgeBase',
  reminders: 'reminderEngine',
  companies: 'company',
  roles: 'roles',
};

const SearchGlyph: React.FC = () => (
  <svg className="sidebar-search-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <circle cx="11" cy="11" r="6.2" stroke="currentColor" strokeWidth="1.9" />
    <path d="M16 16.5L20.2 20.7" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

const flattenHits = (groups: GlobalSearchGroup[]): Array<{ group: GlobalSearchGroup; hit: GlobalSearchHit; index: number }> => {
  const rows: Array<{ group: GlobalSearchGroup; hit: GlobalSearchHit; index: number }> = [];
  groups.forEach((group) => {
    group.items.forEach((hit) => {
      rows.push({ group, hit, index: rows.length });
    });
  });
  return rows;
};

export const SidebarGlobalSearch: React.FC<SidebarGlobalSearchProps> = ({ collapsed }) => {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const sidebarInputRef = React.useRef<HTMLInputElement | null>(null);
  const panelInputRef = React.useRef<HTMLInputElement | null>(null);
  const [draft, setDraft] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [groups, setGroups] = React.useState<GlobalSearchGroup[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({});
  const shortcutLabel = React.useMemo(() => {
    if (typeof navigator === 'undefined') {
      return 'Ctrl K';
    }
    return /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';
  }, []);

  const query = draft.trim();
  const showPanel = open && query.length > 0;
  const rows = flattenHits(groups);

  const updatePanelPosition = React.useCallback(() => {
    const anchor = rootRef.current;
    if (!anchor) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    setPanelStyle({
      top: Math.max(8, rect.top),
      left: rect.right + 10,
      maxHeight: `calc(100dvh - ${Math.max(8, rect.top) + 16}px)`,
    });
  }, []);

  const closePanel = React.useCallback(() => {
    setOpen(false);
    setActiveIndex(0);
  }, []);

  const onDraftChange = (value: string) => {
    setDraft(value);
    setOpen(value.trim().length > 0);
  };

  const focusSearchField = React.useCallback(() => {
    sidebarInputRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (showPanel && collapsed) {
      panelInputRef.current?.focus();
    }
  }, [showPanel, collapsed]);

  React.useEffect(() => {
    if (!showPanel) {
      return;
    }
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [showPanel, collapsed, updatePanelPosition]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        focusSearchField();
        if (query.length > 0) {
          setOpen(true);
        }
      }
      if (event.key === 'Escape' && showPanel) {
        event.preventDefault();
        closePanel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closePanel, focusSearchField, query.length, showPanel]);

  React.useEffect(() => {
    if (!showPanel) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (rootRef.current?.contains(target)) {
        return;
      }
      const panel = document.getElementById('sidebar-global-search-panel');
      if (panel?.contains(target)) {
        return;
      }
      closePanel();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [closePanel, showPanel]);

  React.useEffect(() => {
    if (query.length < MIN_QUERY_LENGTH) {
      setGroups([]);
      setLoading(false);
      setError(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(false);
    const timeoutId = window.setTimeout(() => {
      void searchGlobally(query, controller.signal)
        .then((response) => {
          setGroups(response.groups);
          setActiveIndex(0);
          setLoading(false);
        })
        .catch((err) => {
          if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError') || (err instanceof Error && err.name === 'AbortError')) {
            return;
          }
          setGroups([]);
          setError(true);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [query]);

  const goTo = (href: string) => {
    if (!href) {
      return;
    }
    closePanel();
    navigate(href);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showPanel) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (rows.length === 0 ? 0 : (current + 1) % rows.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (rows.length === 0 ? 0 : (current - 1 + rows.length) % rows.length));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const active = rows[activeIndex];
      if (active) {
        goTo(active.hit.href);
        return;
      }
      if (groups[0]?.href) {
        goTo(groups[0].href);
      }
    }
  };

  const panel = showPanel ? createPortal(
    <div id="sidebar-global-search-panel" className="sidebar-search-panel" style={panelStyle} role="listbox" aria-label={t('searchPlaceholder')}>
      {collapsed ? (
        <div className="sidebar-search-panel-input-wrap">
          <SearchGlyph />
          <input
            ref={panelInputRef}
            className="sidebar-search-panel-input"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={t('searchPlaceholder')}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      ) : null}

      {query.length > 0 && query.length < MIN_QUERY_LENGTH ? (
        <div className="sidebar-search-empty">{t('searchMinChars')}</div>
      ) : null}
      {loading ? <div className="sidebar-search-empty">{t('searchSearching')}</div> : null}
      {error ? <div className="sidebar-search-empty">{t('searchError')}</div> : null}
      {!loading && !error && query.length >= MIN_QUERY_LENGTH && groups.length === 0 ? (
        <div className="sidebar-search-empty">{t('searchNoResults')}</div>
      ) : null}

      {!loading && !error ? groups.map((group) => {
        const labelKey = GROUP_LABEL_KEYS[group.type] ?? group.type;
        return (
          <div key={group.type} className="sidebar-search-group">
            <div className="sidebar-search-group-header">
              <span>{t(labelKey)}</span>
              {group.total > group.items.length ? (
                <button type="button" className="sidebar-search-see-all" onClick={() => goTo(group.href)}>
                  {t('searchSeeAll')}
                </button>
              ) : null}
            </div>
            {group.items.map((hit) => {
              const index = rows.findIndex((row) => row.hit === hit);
              return (
                <button
                  key={`${group.type}-${hit.id}`}
                  type="button"
                  className={`sidebar-search-hit ${index === activeIndex ? 'active' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => goTo(hit.href)}
                >
                  <span className="sidebar-search-hit-title">{hit.title}</span>
                  {hit.subtitle ? <span className="sidebar-search-hit-subtitle">{hit.subtitle}</span> : null}
                </button>
              );
            })}
          </div>
        );
      }) : null}
    </div>,
    document.body,
  ) : null;

  return (
    <div className={`sidebar-search ${collapsed ? 'collapsed' : ''}`} ref={rootRef}>
      {collapsed ? (
        <>
          <button
            type="button"
            className="sidebar-search-icon-btn"
            onClick={focusSearchField}
            title={t('searchOpen')}
            aria-label={t('searchOpen')}
          >
            <SearchGlyph />
          </button>
          <input
            ref={sidebarInputRef}
            className="sidebar-search-sr-input"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onFocus={() => {
              if (query.length > 0) {
                setOpen(true);
              }
            }}
            onKeyDown={onInputKeyDown}
            placeholder={t('searchPlaceholder')}
            autoComplete="off"
            spellCheck={false}
            aria-label={t('searchOpen')}
          />
        </>
      ) : (
        <label className="sidebar-search-field">
          <SearchGlyph />
          <input
            ref={sidebarInputRef}
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onFocus={() => {
              if (query.length > 0) {
                setOpen(true);
              }
            }}
            onKeyDown={onInputKeyDown}
            placeholder={t('searchPlaceholder')}
            autoComplete="off"
            spellCheck={false}
          />
          <span className="sidebar-search-shortcut">{shortcutLabel}</span>
        </label>
      )}
      {panel}
    </div>
  );
};

export default SidebarGlobalSearch;
